// AscendantPage.jsx  (route: /horoscope/ascendant)
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ThemedDatePicker from "./ThemedDatePicker";
import ThemedTimePicker from "./ThemedTimePicker";

export default function AscendantPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // ค่าเริ่มต้น (ถ้ามี query มาก่อนจะ prefill)
  const todayISO = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

  const [birthDate, setBirthDate] = useState(params.get("date") || todayISO);
  const [birthTime, setBirthTime] = useState(params.get("time") || "12:00");
  const [unknownTime, setUnknownTime] = useState(false);

  const canSubmit = useMemo(
    () => !!birthDate && (!!birthTime || unknownTime),
    [birthDate, birthTime, unknownTime]
  );

  // ✅ เปิดเพจแล้วสกรอลไปบนสุดเสมอ
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"; // ปิด auto-restore ของเบราว์เซอร์
    }
    // ใช้ rAF ให้ทำหลัง DOM วาดเสร็จ
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "view_ascendant",
        category: "Ascendant",
        label: "view_ascendant",
      });
    }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // ✅ GTM DataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click_submit_ascendant",
      category: "Ascendant",
      label: "Ascendant Form Submit",
      birth_date: birthDate, // เช่น: "2025-06-29"
      birth_time: birthTime, // เช่น: "15:30"
    });

    // ถ้าไม่ทราบเวลาเกิด ใช้ 12:00 เป็นค่าอ้างอิงกลางวัน (แจ้งผู้ใช้ไว้ด้านล่าง)
    const t = unknownTime ? "12:00" : birthTime;
    navigate(`/horoscope/ascendant/result?date=${birthDate}&time=${t}`);
  };

  return (
    <div className="min-h-screen bg-[#0A0F28] text-white relative overflow-y-auto">
      {/* Header: Back */}
      <header
        className="relative z-10"
        style={{
          position: "absolute",
          paddingTop:
            "calc(env(safe-area-inset-top, 0px) + 10px)" /* เผื่อ notch iPhone */,
        }}
      >
        <div className="px-4"></div>

        <div className="w-full h-20 px-5 xs:px-2 py-2 flex flex-col justify-end">
          <div className="w-full flex justify-between items-center">
            <div>
              <button
                aria-label="ย้อนกลับ"
                onClick={() => navigate("/horoscope")}
                className="inline-flex items-center justify-center w-9 h-9 rounded-2xl
                       bg-white/10 hover:bg-white/15 active:bg-white/20
                       shadow-[0_4px_14px_rgba(0,0,0,0.35)] backdrop-blur-[2px]
                       focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                {/* Chevron Left (SVG) */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 19L8 12L15 5"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div>{/*right*/}</div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 pt-16">
        <div className="w-full flex justify-center mt-2">
          <img
            src="/images/Magic-Crystal-Ball-2.png"
            alt="Crystal Ball"
            width={88}
            className="object-contain drop-shadow-[0_10px_28px_rgba(0,0,0,0.45)]"
          />
        </div>
        <div className="text-center mt-4">
          <h1 className="text-[22px] font-bold">ลัคนาราศี</h1>
          <p className="text-white/70 text-sm">รับคำทำนายรายสัปดาห์</p>
        </div>
      </section>

      {/* ===== Card Form (มีกรอบขาวชัด) ===== */}
      <main className="px-8 mt-4 pb-12 pt-3">
        {/* กรอบขาวนวลรอบการ์ด */}
        <div className="relative rounded-[20px] p-[1.5px] bg-white/80 shadow-[0_12px_28px_rgba(0,0,0,0.45)]">
          {/* ชั้นใน: การ์ดไล่เฉดเข้ม */}
          <section
            className="rounded-[18px] px-5 py-6 text-white overflow-visible"
            style={{
              background: "linear-gradient(180deg,#0E1732 0%, #0B122A 100%)",
            }}
          >
            {/* เส้น ring ฟุ้ง ๆ ซ้อนทับ (เสริมความวาวของขอบ) */}
            <div className="pointer-events-none absolute inset-0 rounded-[20px] ring-1 ring-white/40" />

            <h3 className="text-[#FFD15A] font-bold text-[16px]">
              คำทำนายทำนายลัคนาราศี
            </h3>
            <p className="text-white/80 text-[13px] mt-1">
              กรอกข้อมูลวันเกิดเพื่อคำนวณลัคนาราศีของคุณ
            </p>

            {/* ====== ฟอร์ม ====== */}
            <form onSubmit={onSubmit} className="mt-4 space-y-4">
              {/* วันเกิด */}

              <ThemedDatePicker
                label="วันเกิด"
                value={birthDate}
                onChange={(v) => setBirthDate(v)}
                max={todayISO}
              />

              {/* เวลาเกิด + ปุ่มลัด */}
              <ThemedTimePicker
                value={birthTime}
                onChange={(v) => setBirthTime(v)}
                disabled={unknownTime}
              />

              {/* ปุ่มคำนวณ */}
              <div className="pt-2">
                <button
                  disabled={!canSubmit}
                  className="w-full h-[44px] rounded-full font-bold text-[16px] text-[#1B1300]
                       shadow-[0_8px_22px_rgba(240,170,40,0.35)]
                       disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "linear-gradient(90deg, #FFD86E 0%, #EAAE2C 45%, #CF7B00 100%)",
                  }}
                >
                  ทำนายลัคนาราศี
                </button>
              </div>
              <br />
              <br />

              <p className="text-[12px] text-white/55 text-center mt-[500px]">
                ข้อมูลของคุณจะถูกใช้เพื่อคำนวณลัคนาราศีเท่านั้น
              </p>
            </form>
          </section>
        </div>
      </main>
    </div>
  );
}
